/**
 * Component for resetting the password by the user without the help of a admin, before logging in
 */
import React, { useState } from "react";
import { Paper, Grid, Button, TextField, useTheme, Typography } from "@mui/material";
import jbtLogoBlack from "../assets/jbt-logo-black.png";
import api from "../utils/api";
import { Link } from "react-router-dom";
import { showErrorMessage, showSuccessMessage } from "../utils/toastUtils";

/**
 * Function that allows the user to reset the password, if forgotten by sending them a reset link
 */
const ForgotPassword: React.FunctionComponent = () => {
  const theme = useTheme();

  /**
   * Function which proivdes the styles of the ForgotPassword
   */
  const styles = {
    forgotPassword: {
      width: "65%",
      marginTop: theme.spacing(1),
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
    paper: {
      margin: theme.spacing(3),
      padding: theme.spacing(3),
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
    inputfield: {},
    submit: {
      margin: theme.spacing(3, 0, 1),
      color: "white",
    },
    linkItem: {
      display: "flex",
      justifyContent: "flex-start",
      alignItems: "center",
    },
    warningItem: {
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "center",
    },
    warningText: {
      color: theme.palette.error.main,
      textDecoration: "none",
      pointerEvents: "none",
    },
    jbtLogoBlack: {
      width: "30%",
      height: "auto",
      marginTop: theme.spacing(3),
    },
  };

  const [email, setEmail] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  /**
   * Check if input is an email
   */
  const emailRegexTest = (testEmail: string) => {
    const regex = /^.+@.+\..{1,6}/i;
    return regex.test(testEmail);
  };

  /**
   * Send the email to the backend
   */
  const sendEmailWithLink = async () => {
    if (!emailRegexTest(email)) {
      showErrorMessage("Bitte gib eine gültige E-Mail-Adresse an.");
      return;
    }

    try {
      setIsSubmitting(true);

      const data = { email };
      const res = await api.post("/auth/forgot-password", data);

      if (res.status === 204) {
        showSuccessMessage("Der Link zum Zurücksetzen des Passworts wurde an die angegebene E-Mail-Adresse gesendet.");
      } else {
        showErrorMessage(
          "Der Link zum Zurücksetzen des Passworts konnte nicht an die angegebene E-Mail-Adresse gesendet werden."
        );
      }
    } catch {
      showErrorMessage(
        "Der Link zum Zurücksetzen des Passworts konnte nicht an die angegebene E-Mail-Adresse gesendet werden."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Grid container spacing={0} alignItems="center" justifyContent="center">
      <Grid item xs={10} sm={8} md={6} lg={4}>
        <Paper sx={styles.paper}>
          <img style={styles.jbtLogoBlack} src={jbtLogoBlack} alt="JBT Logo" />
          <h1>Passwort vergessen</h1>

          <form
            id="emailForm"
            onSubmit={(e) => {
              e.preventDefault();
              sendEmailWithLink();
            }}
          >
            <TextField
              sx={styles.inputfield}
              id="email"
              label="E-Mail"
              type="text"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
              fullWidth
            />

            <Button
              sx={styles.submit}
              variant="contained"
              fullWidth
              color="primary"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Wird gesendet..." : "Passwort anfordern"}
            </Button>

            <Button variant="contained" fullWidth color="secondary" component={Link} to={"/login"}>
              Zurück zum Login
            </Button>

            <Typography>
              Um dein Passwort zurückzusetzen, kannst du entweder deine JBT-Mail verwenden, oder eine deiner
              persönlichen E-Mails benutzen (dafür musst du diese in der MDB abgelegt haben).
            </Typography>
          </form>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default ForgotPassword;
